import { Box, Button, Typography } from "@mui/material";
import { Flex, HStack, UserAvatar, colors } from "@zfp-akademie-a-s/zfp-design";
import { UserIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import type { AgentSummary } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { getAgentFullName } from "./utils";

const CARD_RADIUS = "14px";

type Props = {
  readonly sharedWithAgent: AgentSummary | null;
  readonly disabled: boolean;
  readonly onTakeBack: () => void;
};

export const SharingCurrentAdvisor = ({ sharedWithAgent, disabled, onTakeBack }: Props) => {
  const { t } = useResource();

  if (!sharedWithAgent) {
    return (
      <HStack
        alignItems="center"
        spacing={Spacing.L}
        py={Spacing.XL}
        px="18px"
        border="1px dashed"
        borderColor="divider"
        borderRadius={CARD_RADIUS}
      >
        <Flex
          alignItems="center"
          justifyContent="center"
          width={48}
          height={48}
          borderRadius="50%"
          bgcolor={colors.backgroundSecondary}
          color="text.secondary"
        >
          <UserIcon />
        </Flex>
        <Box>
          <Typography variant="body1" fontWeight={500}>
            {t(Resources.AnalysisDetail.Sharing.NotShared.Title)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t(Resources.AnalysisDetail.Sharing.NotShared.Text)}
          </Typography>
        </Box>
      </HStack>
    );
  }

  const fullName = getAgentFullName(sharedWithAgent);

  return (
    <HStack
      alignItems="center"
      justifyContent="space-between"
      spacing={Spacing.L}
      py={Spacing.XL}
      px="18px"
      border={1}
      borderRadius={CARD_RADIUS}
      sx={{ bgcolor: colors.brandLighter, borderColor: colors.brandLightBorder }}
    >
      <HStack alignItems="center" spacing={Spacing.L} minWidth={0}>
        <UserAvatar firstName={sharedWithAgent.firstName} lastName={sharedWithAgent.lastName} color="primary" size="large" />
        <Box minWidth={0}>
          <Typography variant="h3" noWrap>
            {fullName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t(Resources.AnalysisDetail.Sharing.AdvisorId, { advisorId: sharedWithAgent.ssoId })}
          </Typography>
        </Box>
      </HStack>
      <Button variant="contained" disabled={disabled} onClick={onTakeBack} sx={{ flexShrink: 0 }}>
        {t(Resources.AnalysisDetail.Sharing.TakeOver)}
      </Button>
    </HStack>
  );
};
